/**
 * HlsJs Controller - controls the hls.js instance.
 */
'use strict';

import HlsJs from './hlsjs/hls.js';
import Event from './HlsEvents';
import HlsJsEvent from './hlsjs/events';
import { ErrorDetails as HlsJsError } from './hlsjs/errors';
import EventHandlers from './EventHandlers';
import EventEmitter from 'eventemitter2';
import KeySystem from './KeySystems';
import CencMode from './CencModes';

import CustomKeyLoader from './CustomKeyLoader';
import HlsJsKeyLoader from './hlsjs/loader/key-loader';

import Utils from './HlsUtils';
import { logger } from './hlsjs/utils/logger';
require('core-js/fn/array');

/**
* @access internal
*/
class HlsJsController {

//Creation

	static isHlsSupported() {
		return HlsJs.isSupported();
	}

	constructor(config = {}, di = {}) {
		let disableWorkarounds = !!config.disableWorkarounds || false;
		let enableMuxJs = true;
		let decryptOutsideWebworker = false;

		if(config.decryptOutsideWebworker === true) {
			decryptOutsideWebworker = true;
		}

		this._instanceId = config.instanceId;

		this._emitter = di.eventEmitter || new EventEmitter();

		this._hlsJs = di.hlsJs || new HlsJs({
			// we want to control loading
			autoStartLoad: false,

			// we want to control buffer size
			maxBufferSize: 60000000,
			maxBufferLength: 30,
			maxMaxBufferLength: 60,

			enableWorker: !Utils.isFirefoxBadWorker() || disableWorkarounds,
			enableMuxJs,
			decryptOutsideWebworker,

			xhrSetup: this._hlsJsXhrSetup.bind(this),
			obtainKeySystemConfigCallback: this._hlsJsObtainKeySystemConfigCallback.bind(this),
			fetchSetup: function (context, initParams) {
				// Always send cookies, even for cross-origin calls.
				initParams.credentials = 'include';
				return new Request(context.url, initParams);
			},
		});

		this._disposed = false;
		this._inDetach = false;
		this._pendingDispose = false;

		this._handlers = new EventHandlers();

		this._source = '';			// the HLS manifest URL
		this._levels = [];			// from hlsjs manifest parsed data

		// source config properties
		this._startTime = 0;
		this._startTimeByPdt = undefined;
		this._startLevel = -1;
		this._startBitrate = -1;
		this._liveStreamsStartAtLiveTime = true;

		this._fetching = false;				// are we buffering chunks
		this._loaded = false;				// is the manifest parsed

		this._live = undefined;	// default is undefined. ie true.

		this._basePdt = undefined;

		this._slidingWindow = false;
		this._slidingWindowSize = 0;
		this._liveTime = undefined;

		this._bufferedRange = undefined;
		this._networkMetrics = {
			chosenBitrate: null,
			chosenVariant: null,
			perceivedBitrate: null,
			displayTime: null,
		};
		this._nextNetworkMetrics = {
			chosenBitrate: null,
			chosenVariant: null,
			perceivedBitrate: null,
			displayTime: null,
		};

		this._multithreaded = (!Utils.isFirefoxBadWorker() || disableWorkarounds) && (typeof(Worker) !== 'undefined');

		this._audioOnly = false;

		this._mediaAttached = false;		// is the media attached
		this._media = null;

		this._constraints = {
			minVariant : -1,
			maxVariant : -1,
			minBitrate : -1,
			maxBitrate : -1,
			minWidth : -1,
			minHeight : -1,
			maxWidth : -1,
			maxHeight : -1,
		};

		this._timeouts = {
			manifestTimeout : 10000,
			manifestTimeoutMax : 5000, //exponential delay max
			variantTimeout : 10000,
			variantTimeoutMax : 5000,
			chunkTimeout : 20000,
			chunkTimeoutMax : 5000,
			streamTimeout: 30000,
		};

		/* hlsjs defaults
		this._timeouts = {
			manifestTimeout : 10000,
			manifestTimeoutMax : 64000,
			variantTimeout : 10000,
			variantTimeoutMax : 64000,
			chunkTimeout : 20000,
			chunkTimeoutMax : 64000
		}; */

		this._hlsJs.timeouts = this._timeouts;

		this._minimumConstraint = -1;
		this._maximumConstraint = -1;

		this._subtitleRenditions = [];
		this._explicitDimensions = false;	// does manifest explicitly define dimensions

		this._bitrateCycle = false;
		this._bitrateStepDown = false;
		this._bitrateCycleMax = -1;
		this._bitrateCycleMin = -1;

		this._xhrSetup = null;
		this._customKeyLoadCallback = null;
		this._originalKeyLoader = null;
		this._keyCache = null;
		this._obtainKeySystemConfigCallback = null;

		this._fatalErrors = 0;

		this._initialAudioLanguage = null;
		this._initialAudioName = null;
		this._initialAudioIndex = -1;
		this._initialAudioTrackSelected = false;

		//Recovery parameters.
		this._recoveryAudioIndex = -1;
		this._recoveryStartTime = -1;
		this._recoveryStartTimeByPdt = undefined;

		this._hlsJsEvents = [
			{ event: HlsJsEvent.ERROR, 						func: this._onHlsJsError					},
			{ event: HlsJsEvent.MEDIA_ATTACHING, 			func: this._onHlsJsMediaAttaching			},
			{ event: HlsJsEvent.MEDIA_ATTACHED, 			func: this._onHlsJsMediaAttached			},
			{ event: HlsJsEvent.MEDIA_DETACHING, 			func: this._onHlsJsMediaDetaching			},
			{ event: HlsJsEvent.MEDIA_DETACHED, 			func: this._onHlsJsMediaDetached			},

			{ event: HlsJsEvent.MANIFEST_LOADED, 			func: this._onHlsJsManifestLoaded			},
			{ event: HlsJsEvent.MANIFEST_PARSED, 			func: this._onHlsJsManifestParsed			},
			{ event: HlsJsEvent.LEVEL_LOADED, 				func: this._onHlsJsLevelLoaded				},
			{ event: HlsJsEvent.LEVEL_UPDATED, 				func: this._onHlsJsLevelUpdated				},
			{ event: HlsJsEvent.LEVEL_DURATION_UPDATED,		func: this._onHlsJsLevelDurationUpdated		},
			{ event: HlsJsEvent.BUFFER_CREATED,				func: this._onHlsJsBufferCreated			},
			{ event: HlsJsEvent.BUFFER_APPENDED,			func: this._onHlsJsBufferAppended			},
			{ event: HlsJsEvent.BUFFER_FLUSHED,				func: this._onHlsJsBufferFlushed			},
			{ event: HlsJsEvent.LEVEL_SWITCHING,			func: this._onHlsJsLevelSwitching			},
			{ event: HlsJsEvent.FRAG_LOADED, 				func: this._onHlsJsFragLoaded				},
			{ event: HlsJsEvent.FRAG_PARSING_METADATA,		func: this._onHlsJsMetadataParsed			},
			{ event: HlsJsEvent.FRAG_BUFFERED,				func: this._onHlsJsFragBuffered				},
			{ event: HlsJsEvent.TEXT_TRACK_ADDED,			func: this._onHlsJsTextTrackAdded			},
			{ event: HlsJsEvent.AUDIO_TRACKS_UPDATED,		func: this._onHlsJsAudioTracksUpdated		},
			{ event: HlsJsEvent.START_POSITION_SET,			func: this._onHlsJsStartPositionSet			},
			{ event: HlsJsEvent.FPS_DROP_LEVEL_CAPPING,		func: this._onHlsJsFpsDropLevelCapping		},
			{ event: HlsJsEvent.LOGTUBE_LOG,				func: this._onHlsJsLogTube					},
			{ event: HlsJsEvent.FRAG_DATERANGE_UPDATE,		func: this._onHlsJsDateRange				},
			{ event: HlsJsEvent.STREAM_LIVETIME_CHANGED,	func: this._onHlsJsLiveTimeChange			},
			{ event: HlsJsEvent.STREAM_BASEPDT_FOUND,		func: this._onHlsJsBasePdtFound				},
			{ event: HlsJsEvent.BITRATE_ESTIMATE_UPDATED,	func: this._onHlsJsBitrateEstimateUpdated	},
			{ event: HlsJsEvent.LEVEL_SELECTION_FINISHED,	func: this._onHlsJsLevelSelectionFinished	},
			{ event: HlsJsEvent.FRAG_START_FOUND,			func: this._onHlsJsFragStartFound			},
			{ event: HlsJsEvent.SEEK_READY,					func: this._onHlsJsSeekReady				},
		];
		EventHandlers.verifyEventsList(this._hlsJsEvents);

		this._wireHlsJsEvents();

		Utils.applyConfig(this, config);
	}

	dispose() {
		if ( this._disposed || this._pendingDispose ) {
			throw new Error('HlsJsController can not be disposed more than once');
		}

		if ( !this._hlsJs ) {
			throw new Error('HlsJsController.dispose no hlsjs instance');
		}

		// start hlsjs destroy
		if (this._mediaAttached ) {
			this._pendingDispose = true;

			// MEDIA_DETACHED will resolve the pendingDispose
			this._doDetachMedia();


		}
		else {
			this._doDispose();
		}
	}

	_doDispose() {
		this._disposed = true;

		this._unwireHlsJsEvents();
		this._hlsJs.destroy();
		this._hlsJs = null;

		if (this._originalKeyLoader) {
			this._originalKeyLoader.destroy();
		}
		this._originalKeyLoader = null;

		this._constraints = null;
		this._timeouts = null;

		this._source = null;
		this._levels = null;
		this._subtitleRenditions = null;

		this._customKeyLoadCallback = null;

		this._bufferedRange = null;

		this._basePdt = null;
		this._startTimeByPdt = null;

		// this would be our last chance to emit any events... say a DESTROY

		// this will destroy our event handlers too...
		this._handlers.dispose();
		this._handlers = null;

		this._hlsJsEvents = null;

		this._emitter = null;

		this._pendingDispose = false;

		// need to destroy the EventEmitter _ee too.

		// ITS NOW ILLEGAL TO USE HlsJsController.
	}

	flushBuffer(levelFlush) {
		return this._hlsJs.flushBuffers(levelFlush);
	}

	_wireHlsJsEvent({ event, func }) {
		let handler = this._handlers.createEventHandler(this, func);

		this._hlsJs.on(event, handler);
	}

	_unwireHlsJsEvent({ event, func }) {
		let handler = this._handlers.getHandlerForFunction(func);

		if (handler) {
			this._hlsJs.off(event, handler);
		}
	}

	_wireHlsJsEvents() {
		this._hlsJsEvents.forEach(event => this._wireHlsJsEvent(event));
	}

	_unwireHlsJsEvents() {
		this._hlsJsEvents.forEach(event => this._unwireHlsJsEvent(event));
	}

	/**
	 * Calls the clients xhrSetup function if it exists
	 *
	 * Is passed as a configuration parameter to HlsJs, and allows the client
	 * to modify outgoing XHRs.
	 *
	 * @link https://github.com/dailymotion/hls.js/issues/246
	 *
	 * @param {XMLHttpRequest} xhr - the http requested
	 * @param {string} url the url
	 * @param {DataTypes} type type of object being loaded
	 */
	_hlsJsXhrSetup( xhr, url, type ) {
		if( this._xhrSetup ) {
			this._xhrSetup(xhr, url, type);
		}
	}

	_hlsJsObtainKeySystemConfigCallback( keySystem, keySystemData ) {
		if ( this._obtainKeySystemConfigCallback ) {
			return this._obtainKeySystemConfigCallback(keySystem, keySystemData);
		}
		else {
			return null;
		}
	}

	static isSupported() {
		return !!HlsJs.isSupported(); //booleanify
	}

	get hlsJs() {
		return this._hlsJs;
	}

	get emitter() {
		return this._emitter;
	}

	get live() {
		return this._live === undefined || this._live;
	}

	get liveTime() {
		//return this._hlsJs.liveSyncPosition;
		return this._liveTime !== undefined
			? this._liveTime
			: 0;
	}

	get bufferedRange() {
		if( this._bufferedRange !== undefined) {
			return this._bufferedRange;
		}
		else {
			let bufferedRange = [];
			bufferedRange[0] = { start: 0, end: 0, duration: 0 };
			return bufferedRange;
		}
	}

	_emit(event, ...args) {
		this._emitter.emit(event, ...args);
	}

	_setLevels(levels) {
		this._levels = levels;
		this._emit(Event.STREAM_VARIANTS_CHANGED, { variants: levels });
	}



	// note, the parent object contains the currentLevel if we ever want that.
	/*
	PTSKnown
	averagetargetduration
	endSN
	fragments[]
	live
	startSN
	targetduration
	totalduration
	type
	url
	version
	*/
	_processLevelUpdate( levelUpdate ) {
//		logger.log('_processLevelUpdate');
//		logger.log(levelDetails);

		/*
			now lets calculate the liveTime
			The problem is that HlsJs doesn't calculate this until it gets an update to
			its current level. It should be able to calculate it on every load/update, so
			instead we'll reach inside and perform the calculation... which is basically
			duration - liveBuffer + timeSlid.
		*/

		// the assumption is that as the level slides, the start point is updated...
		//let sliding = levelDetails.details.fragments[0].start;
		let sliding = levelUpdate.sliding;

		/*
			computeLivePosition(sliding, levelDetails) {
				let targetLatency = this.config.liveSyncDuration !== undefined
					? this.config.liveSyncDuration
					: this.config.liveSyncDurationCount * levelDetails.targetduration;
				return sliding + Math.max(0, levelDetails.totalduration - targetLatency);
			}
		*/
		let liveSyncPosition = this._hlsJs.streamController.computeLivePosition(sliding, levelUpdate.details );
		//let liveSyncPosition = 0;
		logger.log( `_processLevelUpdate: sliding: ${sliding}, liveSyncPosition: ${liveSyncPosition}`);


		// we want to increase the liveTime before we declare live.
		//this._setLiveTime(liveSyncPosition);
		this._setLive( levelUpdate.details.live);

		/*
			TODO: calculate sliding window based on original MEDIA-SEQUENCE
			HlsJs doesn't bother keeping the original media sequence around. If it did
			it would be a simple matter to compare the new MEDIA-SEQUENCE against the old, and
			then we are sliding. Also, we would then keep the amount slid based on the durations
			that slid off. Instead it always goes back to PTS.
			This means that it picks up differences between levels PTS as sliding.
			The problem is that it produces strange fractional second sliding values either direction
			even for event streams. Just detecting sliding, the below heuristic should be enough to
			filter it, but eventually we will want to add more smarts to HlsJs's playlist management.
		*/

		// sliding > 0 produces false positives, so we assume as long as its at least half a duration, its valid.
		if( sliding >= levelUpdate.details.targetduration / 2) {
			this._setSlidingWindowSize(sliding);
		}


		// TODO: _slidingWindow = sliding > 0; and perhaps stash sliding as well.
		//this._setSlidingWindow(sliding);	// best to send slidingWindow AFTER Live update.
	}

	_setLiveTime( newLiveTime ) {

		if( newLiveTime === undefined || newLiveTime < 0 ) {
			throw new Error( `_setLiveTime: newLiveTime ${newLiveTime} must be valid`);
		}

		// live time should only ever increase.

		if( this._liveTime === undefined || newLiveTime > this._liveTime ) {

			// if its undefined, we actually return 0
			let oldLiveTime = this.liveTime;
			this._liveTime = newLiveTime;

			let data = { liveTime: newLiveTime, lastLiveTime: oldLiveTime };
			logger.log(`STREAM_LIVETIME_CHANGED: { liveTime: ${data.liveTime} lastLiveTime: ${data.lastLiveTime} }`);

			this._emit(Event.STREAM_LIVETIME_CHANGED, data);
		}
	}

	_setLive( newLive ) {

		if( newLive === undefined ) {
			throw new Error( '_setLive: newLive must be valid');
		}

		// we should only be able to go either :
		// undefined -> true -> false
		// undefined -> false

		if( this._live === undefined || (this._live === true && newLive === false) ) {

			// if its undefined, we actually return true, as when it is undefined we assume live
			let oldLive = this.live;
			this._live = newLive;

			let data = { live: newLive, lastLive: oldLive };
			logger.log(`STREAM_ISLIVE_CHANGED: { live: ${data.live} lastLive: ${data.lastLive} }`);

			this._emit(Event.STREAM_ISLIVE_CHANGED, data);
		}
		/*else if( this._live === false && newLive === true ) {
			throw new Error( '_setLive: should be impossible to transition for not-live to live');
		}*/

	}

	/**
	 * @param {boolean} slidingWindow - sliding window true or false.
	 */
	_setSlidingWindow(slidingWindow) {
		if(slidingWindow !== true ) {
			throw new Error( `_setSlidingWindow: slidingWindow can only ever become true. slidingWindow: ${slidingWindow}`);
		}

		if( this._slidingWindow !== slidingWindow ) {
			let oldSlidingWindow = this._slidingWindow;
			this._slidingWindow = slidingWindow;

			let data = { slidingWindow: slidingWindow, lastSlidingWindow: oldSlidingWindow };
			logger.log(`STREAM_ISSLIDINGWINDOW_CHANGED: { slidingWindow: ${data.slidingWindow} lastSlidingWindow: ${data.lastSlidingWindow} }`);

			this._emit(Event.STREAM_ISSLIDINGWINDOW_CHANGED, data);
		}
	}

	_setSlidingWindowSize( slidingWindowSize ) {

		if( slidingWindowSize === undefined ) {
			throw new Error( '_setSlidingWindowSize: slidingWindowSize must be valid');
		}

		//sliding window should only increase.

		if( slidingWindowSize > this._slidingWindowSize ) {
			this._slidingWindowSize = slidingWindowSize;
			if( !this._slidingWindow ) {
				// emits STREAM_ISSLIDINGWINDOW_CHANGED
				this._setSlidingWindow(true);
			}
		}
	}

	_setAudioOnly(audioOnly) {
		if(audioOnly !== this._audioOnly) {
			let data = { audioOnly: audioOnly, lastAudioOnly: this._audioOnly };
			logger.log(`STREAM_ISAUDIOONLY_CHANGED: { audioOnly: ${data.audioOnly} lastAudioOnly: ${data.lastAudioOnly} }`);

			this._audioOnly = audioOnly;

			this._emit(Event.STREAM_ISAUDIOONLY_CHANGED, data);
		}
	}

	_setMultithreaded(multithreaded) {
		if(multithreaded !== false) {
			throw new Error( `_setMultithreaded: multithreaded should only ever become false. multithreaded: ${multithreaded}`);
		}

		if( this._multithreaded !== multithreaded ) {
			let oldMultithreaded = this._multithreaded;
			this._multithreaded = multithreaded;

			let data = { multithreaded: this._multithreaded, lastMultithreaded: oldMultithreaded };
			logger.log(`STREAM_ISMULTITHREADED_CHANGED: { multithreaded: ${data.multithreaded} lastMultithreaded: ${data.lastMultithreaded} }`);

			this._emit(Event.STREAM_ISMULTITHREADED_CHANGED, data);
		}
	}

	_setHlsJsMinMaxBitrate() {
		// these functions are highly dependant on internal behaviour in hlsjs.
		// this capability would be good for unit testing.
		this._setHlsJsMaximumBitrate(this._constraints.maxBitrate);
		this._setHlsJsMinimumBitrate(this._constraints.minBitrate);
	}

	_setHlsJsMaximumBitrate(maxBitrate) {

		// we can only do this after we have levels... and if there is a level.
		if (this._levels && this._levels.length > 0) {
			let maxLevel = this._levels.length - 1;

			// -1 means use maximum bitrate, which is already set.
			if (maxBitrate >= 0) {

				// calculate level which is under or equal to maxBitrate, stopping at the lowest bitrate.
				while (maxLevel > 0 && this._levels[maxLevel].bitrate > maxBitrate) {
					maxLevel--;
				}
			}

			// In HlsJs, autoLevelCapping will clamp the level to this level. It next gets bumped up by minAutoBitrate.
			this._hlsJs.autoLevelCapping = maxLevel;

			// TODO: I think its actually more efficient for us to set the autoLevelCapping to
			// the maximum level for the -1 case rather than to let hlsjs continually re-calculate
			// the maximum level. hls.js will short-circuit to the set value.
		}
	}

	_setHlsJsMinimumBitrate(minBitrate) {

		// In HlsJs, After a level is selected, and then clamped to autoLevelCapping level, it is then bumped up
		// in a loop to satisfy minAutoBitrate.
		//
		// BUT if there is no level that satisfies minAutoBitrate an "internal processing error is triggered
		// in abr-controller.js at line 210
		//
		//		if(minAutoBitrate !== undefined) {
		//			while (levels[nextABRAutoLevel].bitrate < minAutoBitrate) {		// <-- reads past end of array
		//				nextABRAutoLevel++;
		//			}
		//		}
		//
		// The best solution is to ensure that minAutoBitrate is clamped to the maximum levels bitrate.
		// Otherwise who knows what else will blow up in hlsjs.

		// NOTE: this also means that minBitrate OVERRIDES maxBitrate. I'm not sure if we like that
		// behaviour... but we could fix it.

		if (this._levels && this._levels.length > 0) {
			if (minBitrate === -1 || this._levels.length === 1) {
				// this is the no-limit case. By setting undefined, we skip the repetive calculation
				// every bitrate decision. we might as well also do this for a single variant playlist.
				// don't know how smart hlsjs is.
				this._hlsJs.config.minAutoBitrate = undefined;
			}
			else {
				// critical that we clamp to actual max bitrate, or hlsjs will explode.
				this._hlsJs.config.minAutoBitrate = Math.min(minBitrate, this._levels[this._levels.length - 1].bitrate);
			}
		}
	}

	_setBasePdt(pdt) {
		let oldBasePdt = this.basePdt;
		this._basePdt = pdt;

		let data = { basePdt: new Date(this._basePdt), oldBasePdt: oldBasePdt };
		logger.log(`STREAM_BASEPDT_CHANGED: { basePdt: ${data.basePdt} oldBasePdt: ${data.oldBasePdt} }`);

		this._emit(Event.STREAM_BASEPDT_CHANGED, data);
	}

	_setChosenVariant(newVariant) {
		logger.log('HlsJsController::_setChosenVariant');

		if( newVariant === undefined || newVariant < 0 ) {
			throw new Error( `invalid variant: ${newVariant}`);
		}

		if (this._nextNetworkMetrics.chosenVariant !== newVariant) {
			this._nextNetworkMetrics.chosenVariant = newVariant;

			if( this._levels[newVariant].bitrate ) {
				this._setChosenBitrate(this._levels[newVariant].bitrate);
			}
		}
	}

	_setChosenBitrate(newBitrate) {
		logger.log('HlsJsController::_setChosenBitrate');

		if( newBitrate === undefined || newBitrate < 0 ) {
			throw new Error( `invalid bitrate: ${newBitrate}`);
		}

		if (this._nextNetworkMetrics.chosenBitrate !== newBitrate) {
			this._nextNetworkMetrics.chosenBitrate = newBitrate;
		}
	}

	_setPerceivedBitrate(newBitrate) {
		logger.log('HlsJsController::_setPerceivedBitrate');

		if( newBitrate === undefined || newBitrate < 0 ) {
			throw new Error( `invalid bitrate: ${newBitrate}`);
		}

		let roundedBitrate = Math.round(newBitrate);

		if (this._nextNetworkMetrics.perceivedBitrate !== roundedBitrate) {
			this._nextNetworkMetrics.perceivedBitrate = roundedBitrate;
		}
	}

	_updateNetworkMetrics() {
		let nm = this._networkMetrics;
		let nnm = this._nextNetworkMetrics;

		if(nm.displayTime !== nnm.displayTime || nm.perceivedBitrate !== nnm.perceivedBitrate || nm.chosenBitrate !== nnm.chosenBitrate
		|| nm.chosenVariant !== nnm.chosenVariant) {
			this._networkMetrics = {
				chosenVariant: this._nextNetworkMetrics.chosenVariant,
				chosenBitrate: this._nextNetworkMetrics.chosenBitrate,
				perceivedBitrate: this._nextNetworkMetrics.perceivedBitrate,
				displayTime: this._nextNetworkMetrics.displayTime,
			};

			logger.log(`STREAM_NETWORKMETRICS_CHANGED`);
			this._emit(Event.STREAM_NETWORKMETRICS_CHANGED, { networkMetrics: nnm, lastNetworkMetrics: nm });
		}
	}

	_setKeyLoadCallback() {
		if (this._originalKeyLoader === null) {
			this._originalKeyLoader = this._hlsJs.keyLoader;
		}

		if (this._customKeyLoadCallback === null && this._originalKeyLoader === null) {
			this._originalKeyLoader = new HlsJsKeyLoader( this._hlsJs );  //Invalid keyloaders. Make new one.
		}

		if( this._keyCache != null ) {
//			if (this._customKeyLoadCallback !== null) {
//				this._keyCache.downloader = this._customKeyLoadCallback;
//			}
//			else {

			//logger.log(this._hlsJs.config.loader + ' original key loader set *************************************************************');

			this._keyCache.downloader = new this._hlsJs.config.loader();
//			}
			this._hlsJs.keyLoader = new CustomKeyLoader( this._hlsJs, this._keyCache.hlsKeyLoadCallback);
		}
		else if (this._customKeyLoadCallback !== null ) {
			this._hlsJs.keyLoader = new CustomKeyLoader( this._hlsJs, this._customKeyLoadCallback);
		}
		else if (this._hlsJs.keyLoader === null) {
			this._hlsJs.keyLoader = this._originalKeyLoader;
			this._originalKeyLoader = null; //if we keep this we'll double destroy...
		}
	}

	_updateBufferedRange() {
		let bufferedRange = [];
		let bufferTimes = [];

		try {
			/*if (this._hlsJs.bufferController.sourceBuffer.audio !== undefined) {
				bufferTimes = this._hlsJs.bufferController.sourceBuffer.audio.buffered;
			}
			else if (this._hlsJs.bufferController.sourceBuffer.video !== undefined) {
				bufferTimes = this._hlsJs.bufferController.sourceBuffer.video.buffered;
			}*/
			bufferTimes = this._hlsJs.media.buffered;

			if(bufferTimes === undefined || bufferTimes.length === 0) {
				bufferedRange[0] = { start: 0, end: 0, duration: 0 };
			}
			else {
				for(let i = 0; i < bufferTimes.length; i++) {
					bufferedRange[i] = { start: bufferTimes.start(i), end: bufferTimes.end(i),
						duration: bufferTimes.end(i) - bufferTimes.start(i) };
				}
			}

			logger.log('_updateBufferedRange completed');

		}
		catch(err) {
			bufferedRange = [];
			bufferedRange[0] = { start: 0, end: 0, duration: 0 };
		}

		this._bufferedRange = bufferedRange;
	}

	loadSource(url) {
		this._source = url;
		this._loaded = false;
		this._fetching = false;

		//Used to preserve BasePdt in live recovery cases
		this._hlsJs.resumeBasePdt = this._resumeBasePdt;
		this._resumeBasePdt = undefined;

		this._hlsJs.loadSource(this._source);
	}

	attachMedia(video) {
		logger.log('HlsjsController::attachMedia');
		if (this._mediaAttached) {
			throw new Error('HlsjsController::attachMedia media already attached');
		}

		if (video) {
			this._media = video;
			this._hlsJs.attachMedia(video);
		}
	}

	_doDetachMedia() {
		if (!this._mediaAttached) {
			throw new Error('HlsjsController::_doDetachMedia no media attached');
		}

		if (!this._inDetach ) {
			this._inDetach = true;
			this._hlsJs.detachMedia();
		}
		else {
			logger.log('HlsJsController::_doDetachMedia not requesting detach');
		}
	}

	detachMedia() {
		logger.log('HlsJsController::detachMedia()');

		this._doDetachMedia();
	}

	startFetching() {
		if (!this._loaded) {
			throw new Error('stream not loaded');
		}

		if (this._fetching) {
			throw new Error('stream already loading');
		}

		let timeToStartAt = this._startTime;

		if ( this._recoveryStartTime !== -1 ) {
			timeToStartAt = this._recoveryStartTime;
			this._recoveryStartTime = -1;
		}

		this._fetching = true;

		logger.log(`startFetching: { isLive: ${this.live}, LSSALT: ${this._liveStreamsStartAtLiveTime}, startTime: ${timeToStartAt} }`);

		this._hlsJs.startLoad(timeToStartAt, this._liveStreamsStartAtLiveTime);
	}

	stopFetching() {
		if (!this._loaded) {
			throw new Error('stream not loaded');
		}

		if (!this._fetching) {
			throw new Error('stream already not fetching');
		}

		this._fetching = false;

		this._hlsJs.stopFetch();
	}

	resumeFetching(resumePosition = -1) {
		if (!this._loaded) {
			throw new Error('stream not loaded');
		}

		if (this._fetching) {
			throw new Error('stream already fetching');
		}

		this._fetching = true;

		this._hlsJs.resumeFetch(resumePosition);
	}

	_resetState() {
		this._live = undefined;
		this._liveTime = undefined;
		this._audioOnly = false;
		this._slidingWindow = false;
		this._bufferedRange = undefined;
		this._fatalErrors = 0;
		this._basePdt = undefined;
		this._initialAudioTrackSelected = false;
	}

	_onHlsJsError(event, data) {
		if (data.fatal === true) {
			logger.log('HLSJS FATAL ERROR');
			this._hlsJs.stopLoad();
			/* we shouldn't be getting fatal errors unless the stream needs to be shut down
			this._fatalErrors++;
			logger.log('HLSJS FATAL ERROR ' + this._fatalErrors);
			if (this._fatalErrors > 5) {
				logger.log('hlsjs stalled in error loop; calling stopLoad');
				//This does seem needed sometimes, but other times it might make things worse.
				//Could be better to fix issues in hlsjs instead.... - SIEG
				this._hlsJs.stopLoad();
			}
			*/
		}
		else {
			logger.log('HLSJS ERROR');
		}

		if (data.details === HlsJsError.DEMUX_WORKER_ALLOC_ERROR) {
			this._setMultithreaded(false);
		}

		this._emit(Event.HLSJS_ERROR, data);
	}

	_onHlsJsLogTube(event, data) {
		logger.log(data.message);
	}

	_onHlsJsDateRange(event, data) {
		logger.log('HLSJS STREAM_DATERANGE_UPDATE');
		if (data) {
			this._emit(Event.STREAM_DATERANGE_UPDATE, {
				dateRange: data.dateRange,
				startTime: data.startTime,
			});
		}
	}

	_onHlsJsMediaAttached(event, data) {
		logger.log('HLSJS MEDIA_ATTACHED');
		this._mediaAttached = true;

		this._updateBufferedRange(); //in some error cases this isn't cleared.

		this._emit(Event.HLSJS_MEDIA_ATTACHED, data);
	}

	_onHlsJsMediaDetaching() {
		logger.log(`HLSJS MEDIA_DETACHING { mediaAttached: ${this._mediaAttached} }`);
	}

	_onHlsJsMediaAttaching() {
		logger.log(`HLSJS MEDIA_ATTACHING { mediaAttached: ${this._mediaAttached} }`);
	}

	_onHlsJsMediaDetached(/* event, data */) {
		if( this._disposed ) {
			logger.log(`HLSJS MEDIA_DETACHED - already disposed, dropping event`);
		}
		else {
			logger.log(`HLSJS MEDIA_DETACHED { mediaAttached: ${this._mediaAttached}, _inDetach: ${this._inDetach}, pendingDispose: ${this._pendingDispose} }` );
			this._mediaAttached = false;
			this._resetState();

			this._media = null;

			if( this._inDetach ) {
				this._inDetach = false;
				this._emit(Event.HLSJS_MEDIA_DETACHED, { media: null } );
			}

			if (this._pendingDispose) {
				this._doDispose();
			}
		}
	}

	_onHlsJsManifestLoaded(event, data) {
		logger.log('HLSJS MANIFEST_LOADED');

		this._emit(Event.MANIFEST_LOADED, data);
	}

	_onHlsJsManifestParsed(event, data) {
		logger.log(`HLSJS MANIFEST_PARSED`);

		if (this._loaded) {
			throw new Error('manifest is already parsed!');
		}

		// emits event
		this._setLevels(data.levels);

		let newExplicitDimensions = false;

		if(this._levels) {
			this._levels.forEach(function (item) {
				if(item.width !== undefined && item.height !== undefined) {
					newExplicitDimensions = true;
				}
			});
		}

		this._explicitDimensions = newExplicitDimensions;

		this._loaded = true;

		// can only set this after we recieve the levels.
		let newMinMax = this._updateConstraints();
		let startLevel = this._getDefaultLevel();
		this._maximumConstraint = newMinMax.max;
		this._minimumConstraint = newMinMax.min;

		// selects a level based on the selected starting bitrate or level
		if (this._startBitrate !== -1) {
			let startBitrate = this._startBitrate;

			if(this._constraints.minBitrate !== -1) {
				startBitrate = Math.max(startBitrate, this._constraints.minBitrate);
			}

			if(this._constraints.maxBitrate !== -1) {
				startBitrate = Math.min(startBitrate, this._constraints.maxBitrate);
			}

			startLevel = this._setStartLevelUsingBitrate(startBitrate);
		}
		else {
			if (this._startLevel !== -1) {
				startLevel = this._startLevel;
			}

			if(this._minimumConstraint !== -1) {
				startLevel = Math.max(startLevel, this._minimumConstraint);
			}

			if(this._maximumConstraint !== -1) {
				startLevel = Math.min(startLevel, this._maximumConstraint);
			}
		}

		this._hlsJs.startLevel = startLevel;

		this._emit(Event.MANIFEST_PARSED, data );

	}

	_onHlsJsFragBuffered(event, data) {
		logger.log('HLSJS FRAG_BUFFERED');
		this._emit(Event.CHUNK_BUFFERED);

		if(this._bitrateCycle && data.id === 'main') {
			this._cycleBitrates();
		}
	}

	_onHlsJsLiveTimeChange(event, data) {
		this._setLiveTime(data.liveTime);
	}

	_onHlsJsBasePdtFound(event, data) {
		if(Utils.isValidDate(data.basePdt)) {
			this._setBasePdt(data.basePdt);

			if(Utils.isValidDate(this._recoveryStartTimeByPdt)) {
				if (this._recoveryStartTimeByPdt.getTime() > data.basePdt.getTime()) {
					logger.log(`_onHlsJsBasePdtFound: applied this._recoveryStartTimeByPdt: ${this._recoveryStartTimeByPdt}`);
					this._hlsJs.startPositionPdt(this._recoveryStartTimeByPdt);
				}
				else {
					logger.log(`_onHlsJsBasePdtFound: ignoring recovery time as it is too early this._recoveryStartTimeByPdt: ${this._recoveryStartTimeByPdt}`);
				}
				this._recoveryStartTimeByPdt = undefined;
			}
			else if (Utils.isValidDate(this._startTimeByPdt)) {
				//replace the stream coordinator start time with the PDT we have
				this._hlsJs.startPositionPdt(this._startTimeByPdt);
			}
		}
	}

	_cycleBitrates() {
		if(this._bitrateStepDown) {
			if(this.getConstraints().maxVariant === this._bitrateCycleMin) {
				this._bitrateStepDown = false;
				this.setConstraints({ minVariant:this.getConstraints().maxVariant + 1,
					maxVariant:this.getConstraints().maxVariant + 1, noFlush:true });
			}
			else {
				this.setConstraints({ minVariant:this.getConstraints().maxVariant - 1,
					maxVariant:this.getConstraints().maxVariant - 1, noFlush:true });
			}
		}
		else {
			if(this.getConstraints().maxVariant === this._bitrateCycleMax) {
				this._bitrateStepDown = true;
				this.setConstraints({ minVariant:this.getConstraints().maxVariant - 1,
					maxVariant:this.getConstraints().maxVariant - 1, noFlush:true });
			}
			else {
				this.setConstraints({ minVariant:this.getConstraints().maxVariant + 1,
					maxVariant:this.getConstraints().maxVariant + 1, noFlush:true });
			}
		}

		logger.log('HlsJsController::_cycleBitrates: level changed to ' + this.getConstraints().maxVariant);
	}


	_selectInitialAudioTrack(renditions) {
		logger.log('Selecting Initial Audio Track ' + this._initialAudioLanguage + ' ' + this._initialAudioName);

		let renditionCount = renditions.audioTracks.length;
		let i = 0;
		let initialAudioIndex = this._initialAudioIndex;
		let primarySelect = -1;
		let secondarySelect = -1;

		if (this._recoveryAudioIndex !== -1) {
			initialAudioIndex = this._recoveryAudioIndex;
			this._recoveryAudioIndex = -1;
		}

		if (initialAudioIndex > 0 && this._initialAudioIndex < renditionCount) {
			primarySelect = initialAudioIndex;
		}
		else {
			while(i < renditionCount && primarySelect === -1) {
				if (this._initialAudioLanguage !== null && this._initialAudioName !== null) {
					if (this._initialAudioLanguage === renditions.audioTracks[i].lang && this._initialAudioName === renditions.audioTracks[i].name) {
						primarySelect = i;
					}
					else if (secondarySelect === -1 && this._initialAudioName === renditions.audioTracks[i].name) {
						secondarySelect = i;
					}
					else if (secondarySelect === -1 && this._initialAudioLanguage === renditions.audioTracks[i].lang) {
						secondarySelect = i;
					}
				}
				else if (this._initialAudioLanguage !== null) {
					if (this._initialAudioLanguage === renditions.audioTracks[i].lang) {
						primarySelect = i;
					}
				}
				else if (this._initialAudioName !== null) {
					if (this._initialAudioName === renditions.audioTracks[i].name) {
						primarySelect = i;
					}
				}
				i++;
			}
		}

		if (primarySelect !== -1) {
			this.currentAudioRendition = primarySelect;
		}
		else if (secondarySelect !== -1) {
			this.currentAudioRendition = secondarySelect;
		}

	}


	_setStartLevelUsingBitrate(bitrate) {
		let closest = -1;
		let diff = -1;
		let count = 0;

		// find variant with the closest bitrate to what has been requested
		this._levels.forEach(function (item) {
			let newDiff = Math.abs(bitrate - item.bitrate);
			if(diff === -1 || newDiff < diff) {
				diff = newDiff;
				closest = count;
			}
			count++;
		});

		return closest;
	}

	_onHlsJsLevelLoaded(event, data) {
		logger.log('HLSJS LEVEL_LOADED');

		/*if (data.details.fragments[0].rawProgramDateTime) {
			if (this._basePdt === undefined) {
				let basePdt = new Date(Date.parse(data.details.fragments[0].rawProgramDateTime));

				if(Utils.isValidDate(basePdt)) {
					this._setBasePdt(basePdt);

					if (Utils.isValidDate(this._startTimeByPdt)) {
						let startPosition = 0;

						startPosition = (this._startTimeByPdt.getTime() - this._basePdt.getTime()) / 1000;

						this._hlsJs.startPositionPdt(startPosition);
					}
				}
			}
		}*/

		this._emit(Event.VARIANT_LOADED, data);
	}

	_onHlsJsLevelUpdated(event, data) {
		logger.log('HLSJS LEVEL_UPDATED');

		this._emit(Event.VARIANT_UPDATED, data);

		// update duration, liveTime, live, slidingWindow etc (emit events)
		logger.log(data);
		this._processLevelUpdate(data);
	}

	_onHlsJsLevelDurationUpdated(event, data) {
		logger.log('HLSJS LEVEL_DURATION_UPDATED');

		this._emit(Event.VARIANT_DURATION_UPDATED, data);
	}

	_onHlsJsBufferCreated() {
		let audioOnly = true;

		for(let i = 0; i < this._levels.length; i++) {
			if(this._levels[i].videoCodec !== undefined) {
				audioOnly = false;
			}
		}

		this._setAudioOnly(audioOnly);
	}

	_onHlsJsBufferAppended() {
		this._updateBufferedRange();
	}

	_onHlsJsBufferFlushed() {
		this._updateBufferedRange();

		this._emit(Event.BUFFER_FLUSHED);
	}

	_onHlsJsLevelSwitching(event, data) {
		logger.log('HLSJS LEVEL_SWITCHING');

		this._setChosenVariant(data.level);
	}

	_onHlsJsBitrateEstimateUpdated(event, data) {
		this._setPerceivedBitrate(data.bitrate);
	}

	//While this is no longer the main point where metrics update, the initial firing is tied to this event
	_onHlsJsLevelSelectionFinished() {
		this._updateNetworkMetrics();
	}

	_onHlsJsFragStartFound(event, data) {
		this._nextNetworkMetrics.displayTime = this.getTimeForPdt(new Date(data.startPdt));
		this._updateNetworkMetrics();
	}

	_onHlsJsFragLoaded(event, data) {
		logger.log('HLSJS FRAG_LOADED');

		this._emit(Event.CHUNK_LOADED, data);
	}

	_onHlsJsTextTrackAdded(event, data) {
		logger.log('HLSJS TEXT_TRACK_ADDED');

		this._subtitleRenditions.push(data.track);

		this._emit(Event.STREAM_SUBTITLERENDITIONS_CHANGED, data);
	}

	_onHlsJsAudioTracksUpdated(event, data) {
		logger.log('HLSJS AUDIO_TRACKS_UPDATED');

		if (this._initialAudioTrackSelected === false && data.audioTracks.length > 0 &&
		(this._initialAudioLanguage !== null || this._initialAudioName !== null || this._initialAudioIndex !== null)) {
			this._initialAudioTrackSelected = true;
			this._selectInitialAudioTrack(data);
		}

		this._emit(Event.STREAM_AUDIORENDITIONS_CHANGED, data);
	}

	_onHlsJsMetadataParsed(event, data) {
		logger.log('HLSJS FRAG_PARSING_METADATA');
		if (data.frag) {
			this._emit(Event.TIMED_METADATA_UPDATE, {
				id: data.id,
				variant: data.frag.level,
				chunkIdx: data.frag.sn,
				chunkStartTime: data.frag.start,
				chunkEndTime: data.frag.start + data.frag.duration,
				samples: data.samples,
			});
		}
	}

	_onHlsJsStartPositionSet(event, data) {
		logger.log('HLSJS START_POSITION_SET');

		this._emit(Event.STREAM_STARTPOSITION_SET, data);
	}

	_onHlsJsFpsDropLevelCapping(event, data) {
		logger.log('HLSJS FPS_DROP_LEVEL_CAPPING');

		this._constraints.maxVariant = data.level; //we don't want to trigger a constraint recalculation but we do want the change reflected
	}

	_onHlsJsSeekReady(event, data) {
		this._emit(Event.HLSJS_SEEK_READY, data);
	}

	get isMediaAttached() {
		return this._mediaAttached;
	}

	get levels() {
		return this._levels;
	}

	get audioRenditions() {
		return this._hlsJs.audioTracks || [];
	}

	get currentAudioRendition() {
		return this._hlsJs.audioTrack >= 0 ? this._hlsJs.audioTrack : 0;
	}

	set currentAudioRendition(index) {
		Utils.checkNotIntegerAndThrow(index, 'audioRendition');
		Utils.checkLessThanZeroPointZeroAndThrow(index, 'audioRendition');

		if(index >= this.audioRenditions.length) {
			throw new Error('audioRendition can not be greater than ' + (this.audioRenditions.length - 1) + ' for this stream, ( ' + index + ' )');
		}

		let lastAudioRendition = this.currentAudioRendition;

		if(lastAudioRendition !== index) {
			this._hlsJs.audioTrack = index;
			this._emit(Event.STREAM_AUDIORENDITION_CHANGED, { lastAudioRendition: lastAudioRendition, audioRendition: index });
		}
	}

	get subtitleRenditions() {
		return this._subtitleRenditions;
	}

	set changeSubtitlesByIndex(value) {
		Utils.checkNotIntegerAndThrow(value, 'subtitleIndex');
		Utils.checkLessThanOnePointZeroAndThrow(value, 'subtitleIndex');

		let lastSubtitleRendition = this._hlsJs.subtitleIndex;

		if(lastSubtitleRendition !== value) {
			this._hlsJs.subtitleIndex = value;
			this._emit(Event.STREAM_SUBTITLERENDITION_CHANGED, { lastSubtitleRendition: lastSubtitleRendition, subtitleRendition: value });
		}
	}

	get slidingWindow() {
		return this._slidingWindow;
	}

	get audioOnly() {
		return this._audioOnly;
	}

	get multithreaded() {
		return this._multithreaded;
	}

	getConstraints() {
		let constraints = {};

		constraints.minVariant = this._constraints.minVariant;
		constraints.maxVariant = this._constraints.maxVariant;
		constraints.minBitrate = this._constraints.minBitrate;
		constraints.maxBitrate = this._constraints.maxBitrate;
		constraints.minWidth = this._constraints.minWidth;
		constraints.minHeight = this._constraints.minHeight;
		constraints.maxWidth = this._constraints.maxWidth;
		constraints.maxHeight = this._constraints.maxHeight;

		return constraints;
	}

	setConstraints(constraints, causeNoSideEffects) {
		if(!Utils.isObject(constraints)) {
			throw new Error('setConstraints: must use a valid constraints object');
		}
		let newConstraints = this._constraints;
		let flush = false;

		//find and check valid properties, put valid properties into set of changes to make
		//invalid properties can be ignored, valid properties that are < -1 should throw
		if(constraints.minVariant !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.minVariant, 'minVariant');
			Utils.checkLessThanMinusOneAndThrow(constraints.minVariant, 'minVariant');

			newConstraints.minVariant = constraints.minVariant;
		}
		if(constraints.maxVariant !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.maxVariant, 'maxVariant');
			Utils.checkLessThanMinusOneAndThrow(constraints.maxVariant, 'maxVariant');

			newConstraints.maxVariant = constraints.maxVariant;
		}
		if(constraints.minBitrate !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.minBitrate, 'minBitrate');
			Utils.checkLessThanMinusOneAndThrow(constraints.minBitrate, 'minBitrate');

			newConstraints.minBitrate = constraints.minBitrate;
		}
		if(constraints.maxBitrate !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.maxBitrate, 'maxBitrate');
			Utils.checkLessThanMinusOneAndThrow(constraints.maxBitrate, 'maxBitrate');

			newConstraints.maxBitrate = constraints.maxBitrate;
		}
		if(constraints.minWidth !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.minWidth, 'minWidth');
			Utils.checkLessThanMinusOneAndThrow(constraints.minWidth, 'minWidth');

			newConstraints.minWidth = constraints.minWidth;
		}
		if(constraints.minHeight !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.minHeight, 'minHeight');
			Utils.checkLessThanMinusOneAndThrow(constraints.minHeight, 'minHeight');

			newConstraints.minHeight = constraints.minHeight;
		}
		if(constraints.maxWidth !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.maxWidth, 'maxWidth');
			Utils.checkLessThanMinusOneAndThrow(constraints.maxWidth, 'maxWidth');

			newConstraints.maxWidth = constraints.maxWidth;
		}
		if(constraints.maxHeight !== undefined) {
			Utils.checkNotIntegerAndThrow(constraints.maxHeight, 'maxHeight');
			Utils.checkLessThanMinusOneAndThrow(constraints.maxHeight, 'maxHeight');

			newConstraints.maxHeight = constraints.maxHeight;
		}

		//apply new properties after checks so that in case of throw, we don't partially apply new constraints
		this._constraints = newConstraints;
		let newMinMax = this._updateConstraints();

		//if the maximum constraint is relaxed prepare a buffer flush
		if((newMinMax.max > this._maximumConstraint || newMinMax.max === -1) &&
			this._maximumConstraint !== -1) {
			flush = true;
		}

		if(constraints.noFlush === true || causeNoSideEffects) {
			flush = false;
		}

		this._maximumConstraint = newMinMax.max;
		this._minimumConstraint = newMinMax.min;

		if(flush) {
			this._hlsJs.stopFetch();
			this.flushBuffer(true);
			this._hlsJs.resumeFetch();
		}
	}

	getTimeouts() {
		let timeouts = {};

		timeouts.manifestTimeout = this._timeouts.manifestTimeout;
		timeouts.manifestTimeoutMax = this._timeouts.manifestTimeoutMax;
		timeouts.variantTimeout = this._timeouts.variantTimeout;
		timeouts.variantTimeoutMax = this._timeouts.variantTimeoutMax;
		timeouts.chunkTimeout = this._timeouts.chunkTimeout;
		timeouts.chunkTimeoutMax = this._timeouts.chunkTimeoutMax;
		timeouts.streamTimeout = this._timeouts.streamTimeout;

		return timeouts;
	}

	setTimeouts(timeouts) {
		if(!Utils.isObject(timeouts)) {
			throw new Error('setTimeouts: must use a valid timeouts object');
		}

		let newTimeouts = this._timeouts;

		if(timeouts.manifestTimeout !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.manifestTimeout, 'manifestTimeout');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.manifestTimeout, 'manifestTimeout');

			newTimeouts.manifestTimeout = timeouts.manifestTimeout;
		}
		if(timeouts.manifestTimeoutMax !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.manifestTimeoutMax, 'manifestTimeoutMax');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.manifestTimeoutMax, 'manifestTimeoutMax');

			newTimeouts.manifestTimeoutMax = timeouts.manifestTimeoutMax;
		}
		if(timeouts.variantTimeout !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.variantTimeout, 'variantTimeout');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.variantTimeout, 'variantTimeout');

			newTimeouts.variantTimeout = timeouts.variantTimeout;
		}
		if(timeouts.variantTimeoutMax !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.variantTimeoutMax, 'variantTimeoutMax');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.variantTimeoutMax, 'variantTimeoutMax');

			newTimeouts.variantTimeoutMax = timeouts.variantTimeoutMax;
		}
		if(timeouts.chunkTimeout !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.chunkTimeout, 'chunkTimeout');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.chunkTimeout, 'chunkTimeout');

			newTimeouts.chunkTimeout = timeouts.chunkTimeout;
		}
		if(timeouts.chunkTimeoutMax !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.chunkTimeoutMax, 'chunkTimeoutMax');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.chunkTimeoutMax, 'chunkTimeoutMax');

			newTimeouts.chunkTimeoutMax = timeouts.chunkTimeoutMax;
		}
		if(timeouts.streamTimeout !== undefined) {
			Utils.checkNotIntegerAndThrow(timeouts.streamTimeout, 'streamTimeout');
			Utils.checkLessThanZeroPointZeroAndThrow(timeouts.streamTimeout, 'streamTimeout');

			newTimeouts.streamTimeout = timeouts.streamTimeout;
		}

		this._timeouts = newTimeouts;

		this._hlsJs.timeouts = this._timeouts;
	}

	get useAutomaticComplexityReduction() {
		return this._hlsJs.config.capLevelOnFPSDrop;
	}

	set useAutomaticComplexityReduction(value) {
		if(!!value === true && Utils.isTizen()) {
			logger.warn('enabling useAutomaticComplexityReduction will have no effect on Tizen applications');
		}

		this._hlsJs.config.capLevelOnFPSDrop = !!value;
	}

	get bitrateCycle() {
		return this._bitrateCycle;
	}

	set bitrateCycle(value) {
		this._bitrateCycle = !!value;

		if(this._bitrateCycle) {
			this._bitrateStepDown = false;
			this._bitrateCycleMax = this.getConstraints().maxVariant === -1 ? this._levels.length - 1
			: this.getConstraints().maxVariant;
			this._bitrateCycleMin = this.getConstraints().minVariant === -1 ? 0 : this.getConstraints().minVariant;
			this.setConstraints({ minVariant:1, maxVariant:1 });
		}
	}

	// HlsJs appears to be able to handle changing the buffer size by itself, although we should still sanity check to
	// make sure no negative values get in and risk breaking things.
	get maximumBufferSize() {
		return this._hlsJs.config.maxBufferSize;
	}

	set maximumBufferSize(value) {
		Utils.checkNotIntegerAndThrow(value, 'maximumBufferSize');
		Utils.checkLessThanZeroPointZeroAndThrow(value, 'maximumBufferSize');

		this._hlsJs.config.maxBufferSize = value;
	}

	get minimumBufferLength() {
		return this._hlsJs.config.maxBufferLength;
	}

	set minimumBufferLength(value) {
		Utils.checkNotIntegerAndThrow(value, 'minimumBufferLength');
		Utils.checkLessThanOnePointZeroAndThrow(value, 'minimumBufferLength');

		this._hlsJs.config.maxBufferLength = value;
	}

	get maximumBufferLength() {
		return this._hlsJs.config.maxMaxBufferLength;
	}

	set maximumBufferLength(value) {
		Utils.checkNotIntegerAndThrow(value, 'maximumBufferLength');
		Utils.checkLessThanOnePointZeroAndThrow(value, 'maximumBufferLength');

		this._hlsJs.config.maxMaxBufferLength = value;
	}

	get startTime() {
		return this._startTime;
	}

	set startTime(seconds) {
		Utils.checkNotFiniteAndThrow(seconds, 'startTime');
		Utils.checkLessThanZeroPointZeroAndThrow(seconds, 'startTime');

		this._startTime = Number(seconds);
	}

	set recoveryStartTime(seconds) {
		Utils.checkNotFiniteAndThrow(seconds, 'recoveryStartTime');
		Utils.checkLessThanZeroPointZeroAndThrow(seconds, 'recoveryStartTime');

		this._recoveryStartTime = Number(seconds);
	}

	get startTimeByPdt() {
		if (Utils.isValidDate(this._startTimeByPdt) ) {
			return this._startTimeByPdt;
		}
		else {
			return null;
		}
	}

	set startTimeByPdt(time) {
		if (Utils.isValidDate(time)) {
			this._startTimeByPdt = time;
		}
		else {
			throw new Error(`set startTimeByPdt: ${time} is not a valid date`);
		}
	}

	get startLevel() {
		return this._startLevel;
	}

	set startLevel(level) {
		Utils.checkNotIntegerAndThrow(level, 'startVariant');
		Utils.checkLessThanMinusOneAndThrow(level, 'startVariant');

		this._startLevel = level;
	}

	get startBitrate() {
		return this._startBitrate;
	}

	set startBitrate(bitrate) {
		Utils.checkNotIntegerAndThrow(bitrate, 'startBitrate');
		Utils.checkLessThanMinusOneAndThrow(bitrate, 'startBitrate');

		this._startBitrate = bitrate;
	}

	getNetworkMetrics() {
		return this._networkMetrics;
	}

	getDrmCapabilities() {
		// this needs to call into *something* to get the capabilities. And if we need to pend it, then I guess we need to stash the promise while we pend, etc.

		if(!this._drmCapabilitiesPromise) {
			this._drmCapabilitiesPromise = new Promise((resolve, reject) => {
				this._hlsJs.getCdmListAsync()
				.then((cdmList) => {
					//process list
					let processedList = this.generateCapabilities(cdmList);
					resolve(processedList);
				})
				.catch( (err) => {
					reject(err);
				});
			});
		}

		return this._drmCapabilitiesPromise;
	}

	get nudgeOnStall() {
		return this._hlsJs.config.nudgeOnStall;
	}

	set nudgeOnStall(value) {
		if(!!value === false && !this._hlsJs.config.enableMuxJs) {
			throw new Error('Disabling nudgeOnStall while using the legacy demuxer is not allowed');
		}

		this._hlsJs.config.nudgeOnStall = !!value;
	}

	get initialAudioLanguage() {
		return this._initialAudioLanguage;
	}

	set initialAudioLanguage(language) {
		this._initialAudioLanguage = language;
	}

	get initialAudioName() {
		return this._initialAudioName;
	}

	set initialAudioName(name) {
		this._initialAudioName = name;
	}

	get initialAudioIndex() {
		return this._initialAudioIndex;
	}

	set initialAudioIndex(index) {
		this._initialAudioIndex = index;
	}

	set recoveryAudioIndex(index) {
		this._recoveryAudioIndex = index;
	}

	set recoveryStartTimeByPdt(time) {
		if (Utils.isValidDate(time)) {
			this._recoveryStartTimeByPdt = time;
		}
		else {
			throw new Error(`set recoveryStartTimeByPdt: ${time} is not a valid date`);
		}
	}

	/**
	 * if true, and the stream is live, the stream will start at the liveTime
	 *
	 * @type {boolean}
	 */
	get liveStreamsStartAtLiveTime() {
		return this._liveStreamsStartAtLiveTime;
	}

	set liveStreamsStartAtLiveTime(value) {
		this._liveStreamsStartAtLiveTime = !!value;
	}

	/**
	* a callback which is called after creating an XHR request to allow modification of
	* the request. Ie to enable cookie support, or add RequestHeaders.
	*
	* @example
	* hlsJsController.xhrSetup = function (xhr, url) {
	*     xhr.withCredentials = true; // do send cookies
	* }),
	*
	* null means no function is defined. Use null to clear.
	*
	* @type {function(xhr: XMLHttpRequest, url: string)} Xhr Setup Function
	*/
	get xhrSetup() {
		return this._xhrSetup;
	}

	set xhrSetup(func) {
		if( func !== null && !Utils.isFunction(func) ) {
			throw new Error( `xhrSetup: func ${func} must be either null, or a function`);
		}

		this._xhrSetup = func;
	}

	/**
	 * a callback which when called will return a KeySystemConfig
	 *
	 * Not yet implemented
	 */
	get obtainKeySystemConfigCallback() {
		return this._obtainKeySystemConfigCallback;
	}

	set obtainKeySystemConfigCallback(func) {
		if( func !== null && !Utils.isFunction(func) ) {
			throw new Error( `obtainKeySystemConfigCallback: func ${func} must be either null, or a function`);
		}

		this._obtainKeySystemConfigCallback = func;
	}

	/**
	* a callback which is called to acquire a key
	*
	* @example
	* hlsJsController.keyLoadCallback = function (context, callbacks) {
	*     let result = myGetKeyFunction(context.url);
	*     switch(result.code) {
	*     case 'SUCCESS':
	*         callbacks.onSuccess(result.key);
	*         break;
	*     case 'ERROR':
	*         callbacks.onError(result.response, result.error);
	*         break;
	*     case 'TIMEOUT':
	*         callbacks.onTimeout();
	*     }
	* }),
	*
	* null means no function is defined. Use null to clear.
	*
	* @type { function( context, callbacks ) } keyLoadCallback
	*/
	get keyLoadCallback() {
		return this._customKeyLoadCallback;
	}

	set keyLoadCallback(callback) {
		if( callback !== null && !Utils.isFunction(callback) ) {
			throw new Error( `keyLoadCallback: func ${callback} must be either null, or a function`);
		}
		this._customKeyLoadCallback = callback;
		this._setKeyLoadCallback();
	}

	get basePdt() {
		if (Utils.isValidDate(this._basePdt) ) {
			return this._basePdt;
		}
		else {
			return null;
		}
	}


	keyCache(cache) {
		if( cache !== null && !Utils.isFunction(cache.hlsKeyLoadCallback) ) {
			throw new Error( `keyCache: func ${cache.hlsKeyLoadCallback} must be either null, or a function`);
		}

		if (this._keyCache !== null) {
//			this._keyCache.destroy();
			this._keyCache = null;
		}
		this._keyCache = cache;

		//Best place to disable hlsKey. Disable nulling to use hlsKey
		this._keyCache = null;
		//Best place to disable hlsKey. Disable nulling to use hlsKey
		//Note that there are some keyCache tests that should be enabled once this is final

//		//If there's a existing custom callback we do not want to set this
//		if (this._keyLoadCallback === null && this._cache != null) {
//			this._setKeyLoadCallback( this._cache.hlsKeyLoadCallback );
//		}

		if (this._keyCache != null) {
			this._setKeyLoadCallback();
		}
	}

	//max is to be treated as a constraint, min is a preferred quality. Max has
	//priority, but we should always try to play something.
	_updateConstraints() {
		if (this._levels && this._levels.length > 0) {
			let lastLevel = this._levels.length - 1;
			let maxBitrateAsLevel = lastLevel;
			let minBitrateAsLevel = -1;
			let minBitrateAsBitrate = -1;

			// -1 means use maximum bitrate, which is already set.
			if (this._constraints.maxBitrate >= 0) {

				// calculate level which is under or equal to maxBitrate, stopping at the lowest bitrate.
				while (maxBitrateAsLevel > 0 &&
					this._levels[maxBitrateAsLevel].bitrate > this._constraints.maxBitrate) {
					maxBitrateAsLevel--;
				}
			}

			// only apply maxWidth/Height if at least one level has an explicit width and height
			if (this._explicitDimensions) {
				if(this._constraints.maxWidth >= 0) {
					while (maxBitrateAsLevel > 0 &&
						this._levels[maxBitrateAsLevel].width > this._constraints.maxWidth) {
						maxBitrateAsLevel--;
					}
				}
				if(this._constraints.maxHeight >= 0) {
					while (maxBitrateAsLevel > 0 &&
						this._levels[maxBitrateAsLevel].height > this._constraints.maxHeight) {
						maxBitrateAsLevel--;
					}
				}
			}

			//if the set maxVariant is less than the calculated maxBitrateAsLevel then use the maxVariant
			if ((this._constraints.maxVariant >= 0 && this._constraints.maxVariant <= lastLevel)
				&& maxBitrateAsLevel > this._constraints.maxVariant
			) {
				maxBitrateAsLevel = this._constraints.maxVariant;
			}
			// In HlsJs, autoLevelCapping will clamp the level to this level. It next gets bumped up by minAutoBitrate.
			this._hlsJs.autoLevelCapping = maxBitrateAsLevel;
			logger.log('_updateConstraints autoLevelCapping to ' + maxBitrateAsLevel);


			if (this._constraints.minBitrate >= 0 || this._constraints.minVariant >= 0 ||
				this._constraints.minWidth >= 0 || this._constraints.minHeight >= 0) {
				minBitrateAsLevel = 0;
			}

			if (this._constraints.minBitrate >= 0) {
				while (minBitrateAsLevel < lastLevel &&
					this._levels[minBitrateAsLevel].bitrate < this._constraints.minBitrate) {
					minBitrateAsLevel++;
				}
			}

			// only apply minWidth/Height if at least one level has an explicit width and height
			if (this._explicitDimensions) {
				if(this._constraints.minWidth >= 0) {
					while (minBitrateAsLevel < lastLevel &&
						this._levels[minBitrateAsLevel].width < this._constraints.minWidth) {
						minBitrateAsLevel++;
					}
				}
				if(this._constraints.minHeight >= 0) {
					while (minBitrateAsLevel < lastLevel &&
						this._levels[minBitrateAsLevel].height < this._constraints.minHeight) {
						minBitrateAsLevel++;
					}
				}
			}
			if (this._constraints.minVariant >= 0 && minBitrateAsLevel <= this._constraints.minVariant) {
				minBitrateAsLevel = this._constraints.minVariant;
			}

			if (maxBitrateAsLevel !== -1 && minBitrateAsLevel > maxBitrateAsLevel) {
				minBitrateAsLevel = maxBitrateAsLevel;
			}

			if (minBitrateAsLevel >= 0) {
				if (minBitrateAsLevel > lastLevel) {
					minBitrateAsLevel = lastLevel;
				}
				minBitrateAsBitrate = this._levels[minBitrateAsLevel].bitrate;
			}
			if (minBitrateAsBitrate < this._constraints.minBitrate) {
				minBitrateAsBitrate = this._constraints.minBitrate;
			}

			if (maxBitrateAsLevel !== -1 && minBitrateAsBitrate > this._levels[maxBitrateAsLevel].bitrate) {
				minBitrateAsBitrate = this._levels[maxBitrateAsLevel].bitrate;
			}

			if (minBitrateAsBitrate === -1 || this._levels.length === 1) {

				// this is the no-limit case. By setting undefined, we skip the
				// repetive calculation every bitrate decision.  we might as well
				// also do this for a single variant playlist. don't know how smart
				// hlsjs is.
				this._hlsJs.minAutoBitrate = undefined;
			}
			else {
				// critical that we clamp to actual max bitrate, or hlsjs will explode.
				this._hlsJs.minAutoBitrate = Math.min(this._levels[lastLevel].bitrate, minBitrateAsBitrate - 1);
				logger.log('_updateConstraints minAutoBitrate to ' + minBitrateAsLevel);
			}

			//if we try to flush the buffer before hlsjs is actually putting chunks in there hlsjs blows up
			//if(this._fetching) {
				//this._hlsJs.flushBuffers();
			//}
			return { min: minBitrateAsLevel, max: maxBitrateAsLevel };
		}

		return { min: -1, max: -1 };
	}

	_getDefaultLevel() {
		if (this.levels && this.levels.length > 0) {
			for (let i = 0; i < this.levels.length; i++) {
				if (this.levels[i].default) {
					return i;
				}
			}
		}

		return 0;
	}

	getTimeForPdt(pdt) {
		return this._hlsJs.getTimeForPdt(pdt);
	}

	getPdtForTime(time) {
		return this._hlsJs.getPdtForTime(time);
	}

	set bufferBeforeSeeking(seconds) {
		this._hlsJs.bufferBeforeSeeking = seconds;
	}

	generateCapabilities(cdmList) {
		let drmCaps = { keySystems: {} };

		for(let key in KeySystem) {
			if(KeySystem.hasOwnProperty(key)) {
				drmCaps.keySystems[key] = { supported: false, modes: {} };

				for(let mode in CencMode) {
					if(CencMode.hasOwnProperty(mode)) {
						drmCaps.keySystems[key].modes[mode] = false;
					}
				}
			}
		}

		if(cdmList.includes('com.apple.fps.2_0')) {
			drmCaps.keySystems[KeySystem.FAIRPLAY].supported = true;
			drmCaps.keySystems[KeySystem.FAIRPLAY].modes[CencMode.CBC] = true;
		}
		if(cdmList.includes('com.microsoft.playready')) {
			drmCaps.keySystems[KeySystem.PLAYREADY].supported = true;
			drmCaps.keySystems[KeySystem.PLAYREADY].modes[CencMode.CTR] = true;
		}
		if(cdmList.includes('com.widevine.alpha')) {
			drmCaps.keySystems[KeySystem.WIDEVINE].supported = true;
			drmCaps.keySystems[KeySystem.WIDEVINE].modes[CencMode.CTR] = true;
		}
		if(cdmList.includes('org.w3.clearkey')) {
			drmCaps.keySystems[KeySystem.CLEARKEY].supported = true;
			drmCaps.keySystems[KeySystem.CLEARKEY].modes[CencMode.CTR] = true;
		}

		return drmCaps;
	}
}

export default HlsJsController;



// WEBPACK FOOTER //
// ./src/HlsJsController.js